/**
 * AttachmentChip — Displays a single pending attachment in the chat input area.
 *
 * Three visual states: uploading (progress + cancel), ready (icon + name + size + remove), error (red + remove).
 */

import { X, File, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { FileTypeIcon } from '@/components/ui/file-type-icon'
import { SourceIcon } from '@/components/ui/source-icon'
import { formatFileSize } from '@/lib/schemas'
import type { AttachmentStatus, ChatAttachmentState } from '@/types/assistant-attachments'

// ===================
// Props
// ===================

interface AttachmentChipProps {
  filename: string
  mimeType: string
  size: number
  status: AttachmentStatus
  progress: number
  error?: string
  /**
   * Server-side fetches (SharePoint) report no byte progress — the browser never
   * sees the transfer — so the bar pulses instead of filling.
   */
  indeterminate?: boolean
  source?: ChatAttachmentState['source']
  onRemove: () => void
}

// ===================
// Component
// ===================

export function AttachmentChip({
  filename,
  mimeType,
  size,
  status,
  progress,
  error,
  indeterminate,
  source,
  onRemove,
}: AttachmentChipProps) {
  return (
    <div
      className={cn(
        'relative flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs',
        status === 'error'
          ? 'bg-destructive/10 text-destructive'
          : 'bg-muted text-foreground'
      )}
    >
      {/* Icon. Error keeps a plain glyph so it inherits the destructive colour
          rather than fighting it with the file-type tint. */}
      {status === 'uploading' ? (
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
      ) : status === 'error' ? (
        <File className="h-3.5 w-3.5 shrink-0" />
      ) : (
        <FileTypeIcon variant="glyph" mimeType={mimeType} fileName={filename} size={14} />
      )}

      {/* Filename (truncated) */}
      <span className="max-w-[200px] truncate">{filename}</span>

      {/* Provenance — the chip is the only place the user sees where this came
          from, and the tooltip is where the "not imported to the library" scope
          gets said. Suppressed on error: the message owns the row. */}
      {source === 'sharepoint' && status !== 'error' && (
        <span
          title="From SharePoint — attached to this conversation only."
          className="shrink-0 inline-flex"
        >
          <SourceIcon source="sharepoint" size={12} />
        </span>
      )}

      {/* Size / Error */}
      {status === 'error' ? (
        <span className="shrink-0 text-[10px]">{error}</span>
      ) : (
        <span className="shrink-0 text-[10px] text-muted-foreground">
          {status === 'uploading' && indeterminate ? 'Fetching…' : formatFileSize(size)}
        </span>
      )}

      {/* Remove button */}
      <button
        type="button"
        onClick={onRemove}
        className="ml-0.5 shrink-0 rounded-full p-0.5 hover:bg-foreground/10"
        aria-label={`Remove ${filename}`}
      >
        <X className="h-3 w-3" />
      </button>

      {/* Upload progress bar */}
      {status === 'uploading' && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 overflow-hidden rounded-b-lg bg-muted-foreground/20">
          {indeterminate ? (
            <div className="h-full w-full animate-pulse rounded-full bg-[hsl(var(--brand))]" />
          ) : (
            <div
              className="h-full rounded-full bg-[hsl(var(--brand))] transition-all duration-200"
              style={{ width: `${progress}%` }}
            />
          )}
        </div>
      )}
    </div>
  )
}
